import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CloseIconBlock,
  EyeIconClose,
  EyeIconOpen,
  PasswordRegex,
  UserDetails,
  getDecryptedValue,
  getMaxLength,
  HelpIcon,
  ModalCustomStyle,
} from 'common';
import { useFormContext } from 'react-hook-form';
import { useUserPassword } from '@sentara-health/sentara-api-hooks-core';
import {
  Button,
  Label,
  ErrorMessage,
  InputField,
} from '@sentara-health/optimahealth_web';
import PasswordInfoOverlay from '../pages/forms/PasswordInfoOverlay';
import Modal from 'react-modal';

// Define the interface for the props passed to PasswordInputPage component
export interface ChangePasswordProps {
  // Handler for onKeyDown event
  onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  // Password length indicator
  setValue: (value: string) => void;
  setNoContent?: (value: boolean) => void;
  setUnauthError?: (value: boolean) => void;
  setPassCriteria?: (value: boolean) => void;
  setServerError?: (value: boolean) => void;
  showPasswordPlaceholder?: string;
  state?: any;
  setPasswordError?: (value: boolean) => void;
  errorPassword?: boolean;
  showIcon?: boolean;
  inputStrengthClass?: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

// Define the PasswordInputPage component
const ChangePassword: React.FC<ChangePasswordProps> = ({
  onKeyDown,
  setValue,
  setNoContent,
  setUnauthError,
  setPassCriteria,
  setServerError,
  showPasswordPlaceholder,
  state,
  setPasswordError,
  errorPassword,
  showIcon,
  onClick,
  inputStrengthClass,
}) => {
  const { t } = useTranslation();
  const acceptableStrengthClasses = [
    'strength-bar-strong',
    'strength-bar-medium',
  ];
  const methods = useFormContext();
  const {
    getValues,
    formState: { errors },
  } = methods;
  const { userPassword } = useUserPassword();
  // State to manage password visibility
  const [passwordOpen, setPasswordOpen] = useState<boolean>(false);

  const [isOpen, setIsOpen] = useState<boolean>(false);

  //To validate the password Criteria
  const checkPasswordCriteria = async () => {
    const userId: string = await getDecryptedValue(UserDetails.usedId);
    const data: any = {
      firstName: state?.firstName,
      lastName: state?.lastName,
      passwordNew: getValues('password'),
      userId: userId,
    };
    if (getValues('password')) {
      const response = await userPassword(data);
      if (response?.errorCode) {
        handlePasswordError(response?.errorCode);
      } else {
        setPasswordError && setPasswordError(false);
      }
    }
  };

  // Handle password error
  const handlePasswordError = (errorCode: string): void => {
    const errorStatusCode: string[] = [
      '441',
      '400',
      '442',
      '445',
      '439',
      '443',
      '448',
      '422',
      '409',
    ];
    const checkError = errorStatusCode.includes(errorCode.toString());
    if (checkError) {
      setPasswordError && setPasswordError(true);
    } else {
      setPasswordError && setPasswordError(false);
    }
  };
  function openModal(e: { preventDefault: () => void }) {
    e.preventDefault();
    setIsOpen(true);
  }

  function closeModal() {
    setIsOpen(false);
  }

  function changeErrorText() {
    if (window.location.pathname.includes('activation')) {
      return t('common.passwordrequired');
    } else if (window.location.pathname.includes('reset-password')) {
      return t('common.newPasswordRequired');
    } else {
      return t('common.newPassword');
    }
  }

  return (
    <div className="col-12">
      <div className="row">
        {/* Label for the input */}
        <div className="col-12">
          <Label
            dataTestId={'passwordInputLabel'}
            htmlFor="password"
            className="form-label"
          >
            {t('common.newPassword')}
          </Label>
          <span className="text-danger px-1">*</span>

          {/* Container for input and icon */}
          <div className="d-flex align-items-center">
            <div className="position-relative flex-grow-1">
              {/* Input field */}
              <InputField
                className={errors?.password ? 'error-border pe-5' : 'pe-5'}
                autoComplete="password"
                data-openreplay-masked={true}
                placeholder={showPasswordPlaceholder ?? '********'}
                dataTestId="passwordInput"
                maxLength={getMaxLength.password}
                type={!passwordOpen ? 'password' : 'text'}
                onKeyDown={onKeyDown}
                onFocus={() => {
                  if (setNoContent) setNoContent(false);
                  if (setUnauthError) setUnauthError(false);
                  if (setPassCriteria) setPassCriteria(false);
                  if (setServerError) setServerError(false);
                }}
                {...methods.register('password', {
                  required: true,
                  pattern: {
                    value: PasswordRegex,
                    message: 'Password criteria not match',
                  },
                  onBlur: () => {
                    if (window.location.pathname.includes('profile')) {
                      if (
                        acceptableStrengthClasses.includes(
                          inputStrengthClass ?? ''
                        )
                      ) {
                        checkPasswordCriteria();
                      }
                    }
                  },
                  onChange: (e) => {
                    setValue(e.target.value);
                    setPasswordError?.(false);
                  },
                })}
              />
              {/* Button to toggle password visibility */}
              <Button
                id="eyeIconButtonPasswordChange"
                dataTestId="passwordChangeInputEyeIcon"
                variant="icon_btn"
                className="position-absolute top-50 end-0 translate-middle-y"
                data-testid="passwordToggleIcon"
                onClick={(e: { preventDefault: () => void }) => {
                  e.preventDefault();
                  setPasswordOpen(!passwordOpen);
                }}
              >
                {!passwordOpen ? <EyeIconClose /> : <EyeIconOpen />}
              </Button>
            </div>

            {/* Button to open password information modal */}
            <Button
              dataTestId="memIdHelpInfo"
              onClick={openModal}
              aria-label="Information icon click to view"
              variant="icon_btn"
              type={'button'}
              className="ms-2 cursor-pointer"
              iconLeft={<HelpIcon />}
            />
          </div>

          {/* Display error messages */}
          {errors?.password?.type === 'required' && (
            <div className="col-11">
              <ErrorMessage dataTestId={'passwordRequireError'}>
                {changeErrorText()}
              </ErrorMessage>
            </div>
          )}
          {errors?.password?.type === 'pattern' ||
            (window.location.pathname.includes('profile') && errorPassword) ? (
            <div className="col-11">
              <ErrorMessage dataTestId={'passwordRequireError'}>
                {t('common.passwordrequired')}
              </ErrorMessage>
            </div>
          ) : null}
        </div>
      </div>

      {/* Modal for password information */}
      <Modal
        isOpen={isOpen}
        onRequestClose={closeModal}
        data-testid="userNameInputModal"
        contentLabel="Username Information Overlay"
        style={ModalCustomStyle}
      >
        <div className="w-member-overlay" data-testid="userNameInputModalModal">
          <div className="d-flex justify-content-end mb-2">
            <Button
              type="button"
              data-testid="userNameInputModalClose"
              variant="icon_btn"
              onClick={closeModal}
              aria-label="Close"
            >
              <CloseIconBlock />
            </Button>
          </div>
          <PasswordInfoOverlay />
        </div>
      </Modal>
    </div>
  );
};

export default ChangePassword;